// services/ratePerfAssert.ts — jaring submit: order tak boleh menyimpan kurir
// buruk PADAHAL ada opsi lebih baik di rute itu (terukur < minMid, non-Jawa).
// Tutup race "pilih sebelum skor tiba" (insiden ID Express 68% #6f57c474):
// performance null saat submit → cek ulang skor LIVE sekali (cache 6 jam, murah).
// Skor gagal dimuat → lolos (fail-open, checkout tak boleh mati total).
// ATURAN 2026-10-11 (insiden Kotabaru: semua kurir 68% → save macet total):
// tarif ber-flag lowPerfFallback (tak ada opsi ≥ ambang di rute ini) TETAP
// BISA DISIMPAN — termurah + waspada. Insiden 2026-10-04 (#db9b07cc ID Express
// 40% lolos) tetap tertutup: flag hanya ditempel saat T1/T2 kosong
// (gateFallback.ts / rateEligible.ts), bukan saat ada opsi layak diabaikan.
// Hanya yang TAK-TERUKUR (skor tetap tak ditemukan) yang lolos pilihan manual.
import { fetchCourierPerformance, perfKeyFor, isJavaPostal } from './courierPerformance';

export interface PerfCheckRate {
  logistic_name?: string; courier_name?: string; courier?: string;
  performance?: number | null; lowPerfFallback?: boolean; singleCourier?: boolean;
}

export interface PerfCheckOpts {
  // Rute/produk cuma 1 kurir (aturan user 2026-10-20): blokir = jalan buntu →
  // lolos, tapi COD dilarang (hanya Transfer/QRIS).
  singleCourier?: boolean;
  paymentMethod?: string | null;
  // Kurir yang TIDAK boleh dipakai produk (blockedLogistics) — jangan dihitung
  // sebagai "opsi lebih baik" saat memutuskan blokir (insiden caab4283 2026-10-20:
  // 18 layanan disembunyikan produk, tinggal J&T → tetap diblokir "pilih kurir lain").
  isAllowed?: (courierKey: string) => boolean;
}

export function isCODPayment(m: unknown): boolean {
  return String(m || '').toLowerCase().includes('cod');
}

// Hitung kurir berbeda di pool tarif (varian REG/EXPRESS kurir sama = 1).
export function countPoolCouriers(rates: Array<{ rate?: any } | any> | null | undefined): number {
  const keys = new Set<string>();
  for (const item of rates || []) {
    const r = (item as any)?.rate ?? item;
    const k = perfKeyFor(r?.logistic_name || r?.courier_name || r?.courier || r?.logistic_code || '');
    if (k) keys.add(k);
  }
  return keys.size;
}

function transferOnlyMsg(name: string, v: number): string {
  return `Satu-satunya kurir ${name} berkinerja ${v}% — hanya Transfer/QRIS. Ganti metode bayar.`;
}

// Return pesan error (tolak simpan) atau null (lolos).
export async function assertRatePerfLive(rate: PerfCheckRate | null | undefined, loc: { postal?: string; city?: string }, minMid: number, opts?: PerfCheckOpts): Promise<string | null> {
  const r = rate as any;
  if (!r) return null;
  if (isJavaPostal(loc.postal || '')) return null; // Jawa: kurir aktif bebas dipilih
  const single = !!(opts?.singleCourier || r?.singleCourier);
  const courierName = r.logistic_name || r.courier_name || r.courier || 'tersebut';
  // Flag fallback = popup/auto sudah memastikan tak ada opsi ≥ ambang di rute
  // ini saat skor KNOWN → lolos (peringatan sudah tampil di popup/toast).
  // Skor NULL + flag → tetap verifikasi live di bawah (backstop flag basi).
  const fallbackFlag = r.lowPerfFallback === true;
  const known = typeof r.performance === 'number' && r.performance > 0 ? r.performance : null;
  if (known != null) {
    if (fallbackFlag) return null;
    if (known < minMid) {
      if (single) return isCODPayment(opts?.paymentMethod) ? transferOnlyMsg(courierName, known) : null;
      return `Kurir berkinerja ${known}% — di bawah standar. Silakan pilih kurir lain.`;
    }
    return null;
  }
  // Skor belum tiba saat pilih → cek live sekali sebelum menyimpan.
  try {
    const perf = loc.city ? await fetchCourierPerformance(loc.city).catch(() => null) : null;
    const scores = perf?.scores && Object.keys(perf.scores).length ? perf.scores : null;
    if (!scores) return null; // gagal dimuat → fail-open
    const key = perfKeyFor(r.logistic_name || r.courier_name || r.courier || '');
    const v = key && (scores as any)[key] != null ? Number((scores as any)[key]) : null;
    if (v != null && v > 0 && v < minMid) {
      // Rute tanpa opsi baik (skor tertinggi pun < ambang) → lolos darurat
      // seperti flag di atas. Tolak hanya bila ADA opsi lebih baik — dan opsi itu
      // HARUS kurir yang boleh dipakai produk ini (bukan yang diblokir).
      const allowedScores = Object.entries(scores)
        .filter(([k]) => !opts?.isAllowed || opts.isAllowed(k))
        .map(([, x]) => Number(x) || 0);
      const best = allowedScores.length ? Math.max(...allowedScores) : 0;
      if (best < minMid) return null;
      if (single) return isCODPayment(opts?.paymentMethod) ? transferOnlyMsg(courierName, v) : null;
      return `Kurir berkinerja ${v}% — di bawah standar. Silakan pilih kurir lain.`;
    }
  } catch { /* best-effort: lolos, jaring di booking */ }
  return null;
}
